import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { errors, urls } from '../../../constants';
import { isBoolean } from '../../../utils/validations';
import { isDirectorComplete } from '../../../utils/completes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Controls from '../../Controls';
import Title from '../../Title';
import QuestionCard from '../../QuestionCard';
import { PhoneInputField } from '../../PhoneInputField';

import { Lock, GripVertical, User, UserCheck, UserLock, Trash2 } from 'lucide-react';
import { Box, Checkbox, DialogContent, Label, InfoPanel, TextField, Typography } from '@otcmarketsgroup/otcm-ui-lib';

const question = {
    title: 'Independent Director',
    subTitle: () => <p>Is this individual an  <a href={urls.GLOSSARY_IND_DIR} target='_blank' rel='noopener noreferrer'>Independent Director</a>?</p>,
    fieldName: 'isIndependent'
};

const DirectorForm = ({ person, onPersonAdd, onPersonUpdate, isChairPerson, onCancel, onValueChanged, isAdd, isReadOnly }) => {
    const [validationError, setValidationError] = useState(false);

    const handleQuestionUpdate = (value, field) => {
        person[field] = value;
        onValueChanged(person);
    };

    const handleSubmitPerson = () => {
        setValidationError(false);

        const onSubmit = (!person.id) ? onPersonAdd : onPersonUpdate;
        const director = person;
        if (!director.isDirector) director.isDirector = true;
        if (isChairPerson && !director.isChairPerson) director.isChairPerson = true;

        if (!isDirectorComplete(person)) {
            setValidationError(true);
            return;
        }

        onSubmit(director);
    };

    const handleTextChange = (field, event) => {
        person[field] = event?.target?.value;
        onValueChanged(person);
    };

    const handleCheckChange = (field, event) => {
        person[field] = event.target.checked;
        onValueChanged(person);
    };

    const onPhoneValueChanged = (phone, data) => {
        person.phone = phone;
        person.phoneCountryCode = data.countryCode.toUpperCase();
        onValueChanged(person);
    };

    return (
        <Box>
            <DialogContent>
                <Box display='flex' flexDirection='column' gap={3}>
                    <InfoPanel title="Private Information" icon={<Lock size="20px" color="var(--otc-green)" />}>
                        This icon denotes that the information is private and is not publicly displayed.
                    </InfoPanel>
                    <Typography variant="body1">
                        Provide legal name, board position and contact information below.
                    </Typography>
                    <Box display='flex' flexDirection='column' gap={2}>
                        <TextField
                            label="First Name"
                            placeholder="Enter text here"
                            showClearButton
                            value={person?.firstName}
                            maxLength={75}
                            readOnly={isReadOnly}
                            error={(validationError || isReadOnly) && !person?.firstName}
                            helperText={(validationError || isReadOnly) && !person?.firstName ? errors.INCOMPLETE_DATA_FIELD : ""}
                            onChange={(event) => handleTextChange('firstName', event)} />
                        <TextField
                            label="Middle Name"
                            placeholder="Enter text here"
                            maxLength={75}
                            showClearButton
                            value={person?.middleName}
                            readOnly={isReadOnly}
                            onChange={(event) => handleTextChange('middleName', event)} />
                        <TextField
                            label="Last Name"
                            placeholder="Enter text here"
                            showClearButton
                            value={person?.lastName}
                            maxLength={75}
                            readOnly={isReadOnly}
                            error={(validationError || isReadOnly) && !person?.lastName}
                            helperText={(validationError || isReadOnly) && !person?.lastName ? errors.INCOMPLETE_DATA_FIELD : ""}
                            onChange={(event) => handleTextChange('lastName', event)} />
                        <Box>
                            <Label startIcon={<Lock size="16px" color="var(--otc-green)" />}>
                                Phone Number
                            </Label>
                            <PhoneInputField
                                value={person?.phone}
                                country={person?.phoneCountryCode?.toLowerCase()}
                                disabled={isReadOnly}
                                onChange={onPhoneValueChanged} />
                        </Box>
                        <Box>
                            <Label startIcon={<Lock size="16px" color="var(--otc-green)" />}>
                                Email
                            </Label>
                            <TextField
                                placeholder="Enter text here"
                                showClearButton
                                value={person?.email}
                                maxLength={75}
                                readOnly={isReadOnly}
                                onChange={(event) => handleTextChange('email', event)} />
                        </Box>
                    </Box>
                    <QuestionCard
                        item={question}
                        section={person}
                        readOnly={isReadOnly}
                        isValid={validationError ? isBoolean(person.isIndependent) : true}
                        handleQuestionClick={handleQuestionUpdate} />
                    <Typography variant="body1">
                        Select all Committee Memberships that apply to this Director.
                    </Typography>
                    <Box display='flex' flexDirection='column' gap={2}>
                        <Checkbox
                            label='Audit Committee'
                            checked={person?.isAuditCommittee}
                            disabled={isReadOnly}
                            onChange={e => handleCheckChange('isAuditCommittee', e)}
                        />
                        <Checkbox
                            label='Nominating Committee'
                            checked={person?.isNominatingCommittee}
                            disabled={isReadOnly}
                            onChange={e => handleCheckChange('isNominatingCommittee', e)}
                        />
                        <Checkbox
                            label='Compensation Committee'
                            checked={person?.isCompensationCommittee}
                            disabled={isReadOnly}
                            onChange={e => handleCheckChange('isCompensationCommittee', e)}
                        />
                    </Box>
                    <Box display='flex' flexDirection='column' gap={2}>
                        <TextField
                            label="LinkedIn Profile"
                            placeholder="Enter text here"
                            showClearButton
                            value={person?.linkedInId}
                            maxLength={75}
                            readOnly={isReadOnly}
                            onChange={(event) => handleTextChange('linkedInId', event)} />
                        <TextField
                            label="X Profile"
                            placeholder="Enter text here"
                            maxLength={75}
                            showClearButton
                            value={person?.twitterId}
                            readOnly={isReadOnly}
                            onChange={(event) => handleTextChange('twitterId', event)} />
                        <TextField
                            label="Facebook Profile"
                            placeholder="Enter text here"
                            maxLength={75}
                            showClearButton
                            value={person?.facebookId}
                            readOnly={isReadOnly}
                            onChange={(event) => handleTextChange('facebookId', event)} />
                        <Box>
                            <TextField
                                label="Biography"
                                placeholder="Enter text here"
                                multiline
                                maxLength={75}
                                showCharCounter
                                maxLength={1500}
                                value={person?.biography}
                                readOnly={isReadOnly}
                                onChange={(event) => handleTextChange('biography', event)} />
                        </Box>
                    </Box>
                    {validationError && <InfoPanel isWarning title={errors.INCOMPLETE_DATA} />}
                </Box>
            </DialogContent>
            {!isReadOnly && <Controls
                className='mtXXL'
                cancelText='Cancel'
                submitText={isAdd ? 'Add' : 'Update'}
                onCancelClick={onCancel}
                onSubmitClick={handleSubmitPerson}
            />}
        </Box>
    );
};

DirectorForm.propTypes = {
    person: PropTypes.object,
    onCancel: PropTypes.func,
    onPersonAdd: PropTypes.func,
    onPersonUpdate: PropTypes.func,
    onValueChanged: PropTypes.func,
    isChairPerson: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    isAdd: PropTypes.bool
};

export default DirectorForm;
